import React, { useState, useEffect } from "react";
import { Button, Switch } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Loader from "../loader/Loader";
import AddUnit from "./AddUnit";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeUnitList,
} from "../../redux/counterSlice";
import EditIcon from "@mui/icons-material/Edit";
import { grey } from "@mui/material/colors";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import { usePermission } from "../../hooks/usePermission";

const Units = () => {
  const dispatch = useDispatch();
  const permissions = usePermission("unit");
  // Use the custom hook for pagination/search state
  const {
    page: pageNo,
    rowsPerPage: pageSize,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("unit", 10);
  const tableHeight = useTableHeight();

  const counterState = useSelector((state) => state.counter);
  const unitList = counterState.unitList || [];
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const [loader, setLoader] = useState(false);
  const methods = useForm();
  const { reset } = methods;
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [unitsLocal, setUnitsLocal] = useState([]);

  // Load paginated unit data from API and store in Redux
  const loadInitialData = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getAllUnitList");
      dispatch(storeUnitList(response.data?.data || []));
    } catch (err) {
      toast.error(err?.message || "Failed to fetch unit list");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  // Load all active units locally (for dropdown in AddUnit modal)
  const loadProducts = () => {
    setLoader(true);
    axios
      .get("/getAllActiveUnitList")
      .then((response) => {
        setUnitsLocal(response?.data?.data || []);
      })
      .catch((err) => {
        toast.error(err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    loadInitialData();
  }, [pageNo, pageSize]);

  // Toggle unit status
  const toggleStatus = (unit_id, currentStatus) => {
    setLoader(true);

    const apiEndpoint =
      currentStatus === 1
        ? `/deActivateUnit/${unit_id}`
        : `/activateUnit/${unit_id}`;

    axios
      .get(apiEndpoint)
      .then(() => {
        toast.success(
          currentStatus === 1
            ? "Unit deactivated successfully"
            : "Unit activated successfully"
        );
        loadInitialData();
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to toggle status");
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  // Delete unit handler
  const deleteClickHandler = async (id) => {
    try {
      if (!id) return;

      const response = await axios.get(`/deleteUnit/${id}`);

      if (response?.data?.error) {
        let errDesc = response?.data?.message || "Something went wrong";

        if (response?.data?.data) {
          errDesc = Object.values(response?.data?.data).join("\n");
        }

        dispatch(showModal({ msg: errDesc, error: true }));
      } else {
        toast.success(response?.data?.message || "Deleted successfully");
        loadInitialData();
        handleClose();
      }
    } catch (error) {
      dispatch(
        showModal({
          msg:
            error?.response?.data?.message || error?.message || "Server Error",
          error: true,
        })
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  // Open modal for editing a unit
  const editClickHandler = (item) => {
    loadProducts();
    setSelectedRow({ ...item });
    setOpen(true);
  };

  // Close modal and reset form
  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  // Filter units locally by searchText (case-insensitive)
  const filteredUnitList = unitList.filter((unit) =>
    unit.unit_name.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
        filter: false,
      },
    },
    {
      name: "unit_name",
      label: "Unit Name",
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const unit = filteredUnitList[dataIndex];
          const status = unit?.active_status;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() =>
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate" : "Activate",
                    action: () => toggleStatus(unit.unit_id, status),
                  })
                )
              }
              style={{ marginBottom: "5px" }}
            />
          );
        },
        filter: true,
        filterOptions: {
          names: ["Active", "Inactive"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Active") && +value === 1) return false;
              if (filters.includes("Inactive") && +value === 0) return false;
              return true;
            }
            return false;
          },
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const status = filteredUnitList[dataIndex]?.active_status;
                return (
                  <div className="actionBtns">
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1}
                        aria-label="edit"
                        onClick={() =>
                          editClickHandler(filteredUnitList[dataIndex])
                        }
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{ color: status === 1 ? grey[600] : "secondary" }}
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={status === 1}
                        aria-label="delete"
                        onClick={() =>
                          dispatch(
                            showConfirmModal({
                              msg: "Delete",
                              action: () =>
                                deleteClickHandler(
                                  filteredUnitList[dataIndex].unit_id
                                ),
                            })
                          )
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: status === 1 ? grey[600] : "red" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: pageSize,
    page: pageNo,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Units</h4>

          <MUIDataTable
            data={filteredUnitList.length > 0 ? filteredUnitList : []}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    loadProducts();
                    setSelectedRow(null);
                    setOpen(true);
                    reset();
                  }}
                  startIcon={<AddIcon />}
                >
                  New Unit
                </Button>
              ) : null
            }
          />
        </>
      )}

      {unitsLocal?.length > 0 && (
        <AddUnit
          data={unitsLocal}
          open={open}
          handleClose={handleClose}
          selectedRow={selectedRow}
          loadInitialData={loadInitialData}
        />
      )}
    </>
  );
};

export default Units;
